import * as React from "react";
import {observer} from "mobx-react";
import type {Lambda} from "mobx";
import {makeObservable, observable, reaction, runInAction} from "mobx";
import type {BeforeCapture, DragStart, DragUpdate, DropResult, ResponderProvided} from "@hello-pangea/dnd";
import {Draggable, Droppable, DragDropContext} from "@hello-pangea/dnd";
import {withAppStores} from "../../../../../../AppStores";
import {PopupUtils} from "../../../popups/PopupUtils";
import type {Catalog} from "../../../../../../data/models/Catalog";
import type {Xyicon} from "../../../../../../data/models/Xyicon";
import type {PortDataDto, PortTemplateDto, XyiconLinkDetail} from "../../../../../../generated/api/base";
import type {AppState} from "../../../../../../data/state/AppState";
import {PortComponent} from "../../../../catalog/port/PortComponent";
import {Button} from "../../../../../widgets/button/Button";
import {XyiconFeature, LinkType, Permission} from "../../../../../../generated/api/base";
import type {Link} from "../../../../../../data/models/Link";
import type {IModel} from "../../../../../../data/models/Model";
import type {App} from "../../../../../../App";
import type {AppActions} from "../../../../../../data/state/AppActions";
import {LinkBreakers} from "../../../../space/spaceeditor/ui/actionbar/LinkBreakers";
import {ReactUtils} from "../../../../../utils/ReactUtils";
import {ArrayUtils} from "../../../../../../utils/data/array/ArrayUtils";
import {DRAGGABLE_ID_SEPARATOR, FULL_LIST, Port} from "../../../../catalog/port/Port";
import {PortComponentLink} from "../../../../catalog/port/PortComponentLink";
import {CrossPortfolioXyicon} from "../../../../space/spaceeditor/ui/actionbar/CrossPortfolioXyicon";
import {PortLayoutType} from "./PortLayoutType";

const orphanedDroppableId = "orphaned";

// we modify the link by deleting it and creating a new one. We wait this many ms after the deletion,
// because there might be a follow-up create request coming as well,
// causing a little flash / disappearance of port-links for a split-second
const waitAmountBeforeUIUpdate = 150;

const getObjectPortfolioId = (link: Link, objectId: string, fallbackPortfolioId: string) => {
	if (link.objectAID === objectId) {
		return link.objectAPortfolioID ?? fallbackPortfolioId;
	}
	if (link.objectBID === objectId) {
		return link.objectBPortfolioID ?? fallbackPortfolioId;
	}
	return fallbackPortfolioId;
};

export interface IPortMap {
	[portId: string]: {xyiconId: string; link: Link}[];
}

interface IPortsProps {
	readonly item: Catalog | Xyicon;
	readonly layout?: PortLayoutType;
	readonly onEditPortTemplateClick?: (() => void) | null;
	readonly onLabelChange?: (newValue: string, portId: string) => Promise<void>;
	readonly app?: App;
	readonly appState?: AppState;
}

interface IPortsState {
	grabbedXyiconId: string;
	grabbedItemDraggableId: string;
}

@withAppStores("app", "appState")
@observer
export class Ports extends React.Component<IPortsProps, IPortsState> {
	private _updateTimeoutId: number = -1;
	private _portMap: {labelOverrides: PortDataDto[]; portMap: IPortMap} = {labelOverrides: [], portMap: {}};
	private _orphanedList: Link[] = [];
	private _activeRequests: number = 0;
	private _isBreakLinksPopupWindowOpen = false;
	private _disposer: Lambda;

	constructor(props: IPortsProps) {
		super(props);
		makeObservable<this, "_portMap" | "_orphanedList">(this, {
			_portMap: observable,
			_orphanedList: observable,
		});
		this.state = {
			grabbedXyiconId: "",
			grabbedItemDraggableId: "",
		};
	}

	public static readonly defaultProps: Partial<IPortsProps> = {
		layout: PortLayoutType.Icon,
	};

	private get catalogPermission() {
		return this.props.appState.user?.getOrganizationPermission(XyiconFeature.XyiconCatalog);
	}

	private onPortTemplateChange = () => {
		this._orphanedList = this.getOrphanedXyiconLinks(this.props);
	};

	private getOrphanedXyiconLinks(props: IPortsProps): Link[] {
		const {item, appState} = props;

		if (item.ownFeature !== XyiconFeature.Xyicon) {
			return [];
		} else {
			return appState.actions.getList<Link>(XyiconFeature.Link).filter((link: Link) => {
				if (link.objectALinkType === LinkType.Xyicon && link.objectBLinkType === LinkType.Xyicon && !link.isEmbedded) {
					if (!!(item as Xyicon).ports.length && (item.id === link.objectAID || item.id === link.objectBID)) {
						const xyicon = item as Xyicon;

						if (xyicon.id === link.objectAID) {
							return !!link.objectAPortID && !this.isPortIdInPorts(link.objectAPortID, xyicon.ports);
						} else if (xyicon.id === link.objectBID) {
							return !!link.objectBPortID && !this.isPortIdInPorts(link.objectBPortID, xyicon.ports);
						}
					}
				}

				return false;
			});
		}
	}

	private static getPortMapAndLabelOverrides(feature: XyiconFeature, item: IModel, actions: AppActions) {
		const portMap: IPortMap = {};
		const labelOverrides: PortDataDto[] = [];

		if (feature === XyiconFeature.Xyicon) {
			labelOverrides.push(...(item as Xyicon).portData);

			const itemId = item.id;
			const xyiconLinks = actions
				.getList<Link>(XyiconFeature.Link)
				.filter(
					(link: Link) =>
						((itemId === link.objectBID && link.objectBPortID) || (itemId === link.objectAID && link.objectAPortID)) &&
						link.objectALinkType === LinkType.Xyicon &&
						link.objectBLinkType === LinkType.Xyicon &&
						actions.getFeatureItemById<Xyicon>(link.objectAID, XyiconFeature.Xyicon) &&
						actions.getFeatureItemById<Xyicon>(link.objectBID, XyiconFeature.Xyicon),
				);

			for (const xyiconLink of xyiconLinks) {
				let directionA: "objectA" | "objectB" = "objectB";
				let directionB: "objectA" | "objectB" = "objectA";

				if (itemId === xyiconLink.objectAID) {
					[directionA, directionB] = [directionB, directionA];
				}

				const recordsMaybe = portMap[xyiconLink[`${directionA}PortID`]];

				if (recordsMaybe) {
					recordsMaybe.push({
						xyiconId: xyiconLink[`${directionB}ID`],
						link: xyiconLink,
					});
				} else {
					portMap[xyiconLink[`${directionA}PortID`]] = [
						{
							xyiconId: xyiconLink[`${directionB}ID`],
							link: xyiconLink,
						},
					];
				}
			}

			const crossPortfolioXyiconLinks = actions.getCrossPortfolioLinksXyiconXyicon(itemId);

			for (const linkObject of crossPortfolioXyiconLinks) {
				if (linkObject.onePortId) {
					const recordsMaybe = portMap[linkObject.onePortId];

					if (recordsMaybe) {
						recordsMaybe.push({
							xyiconId: linkObject.crossPortfolioXyiconId,
							link: linkObject.link,
						});
					} else {
						portMap[linkObject.onePortId] = [
							{
								xyiconId: linkObject.crossPortfolioXyiconId,
								link: linkObject.link,
							},
						];
					}
				}
			}
		}

		return {
			portMap,
			labelOverrides,
		};
	}

	private async breakLinks(links: Link[]) {
		if (!this._isBreakLinksPopupWindowOpen) {
			const count = links.length;

			this._isBreakLinksPopupWindowOpen = true;
			const confirmed = await PopupUtils.getBreakLinksConfirmationPopup(count);

			this._isBreakLinksPopupWindowOpen = false;

			if (confirmed) {
				runInAction(async () => {
					await LinkBreakers.breakLinks(
						this.props.appState.app.spaceViewRenderer.transport,
						links.map((l) => l.id),
						false,
						(this.props.item as Xyicon).portfolioId,
					);
					this.updateLocalLists(this.props);
				});
			}
		}
	}

	private isPortIdInPorts(id: string, ports: PortTemplateDto[]): boolean {
		return !!Port.getPortById(ports, id);
	}

	private movePortComponentLink = (dragUpdate: DragUpdate | DropResult) => {
		const {source, destination, draggableId} = dragUpdate;
		const {item, app, appState} = this.props;
		const {linkManager} = app.spaceViewRenderer.toolManager;
		const linkId = draggableId.split(DRAGGABLE_ID_SEPARATOR)[0];
		const xyiconId = draggableId.split(DRAGGABLE_ID_SEPARATOR)[1];
		const existingLinkMaybe = appState.actions.getFeatureItemById<Link>(linkId, XyiconFeature.Link);
		const {portMap} = Ports.getPortMapAndLabelOverrides(item.ownFeature, item, appState.actions);
		const sourceDroppableId = source.droppableId.split(FULL_LIST)[0];

		if (existingLinkMaybe && destination) {
			const itemPortfolioId = getObjectPortfolioId(existingLinkMaybe, item.id, (item as Xyicon).portfolioId ?? appState.activePortfolioId);
			const linkedXyicon = appState.actions.getFeatureItemById<Xyicon>(xyiconId, XyiconFeature.Xyicon);
			const linkedXyiconPortfolioId = getObjectPortfolioId(existingLinkMaybe, xyiconId, linkedXyicon?.portfolioId ?? itemPortfolioId);

			if (destination.droppableId !== orphanedDroppableId && source.droppableId === orphanedDroppableId) {
				this.forceUpdate();

				// Handle link moving in the virtual list
				const destinationPort = this._portMap.portMap[destination.droppableId] || [];

				this._orphanedList = ArrayUtils.remove(
					this._orphanedList,
					this._orphanedList.find((ol) => ol.objectAID === draggableId || ol.objectBID === draggableId),
				);
				this._portMap.portMap[destination.droppableId] = ArrayUtils.add(destinationPort, {xyiconId, link: existingLinkMaybe});

				runInAction(async () => {
					const promises: Promise<any>[] = [];

					promises.push(LinkBreakers.breakLinks(app.spaceViewRenderer.transport, [existingLinkMaybe.id], false, itemPortfolioId));

					const createData: XyiconLinkDetail[] = [
						{
							objectAID: item.id,
							objectBID: xyiconId,
							objectAPortID: destination.droppableId,
							objectBPortID:
								(existingLinkMaybe?.objectAID === xyiconId ? existingLinkMaybe?.objectAPortID : existingLinkMaybe?.objectBPortID) || null,
							isEmbedded: item.ownFeature === XyiconFeature.Xyicon ? item.isEmbedded : false,
						},
					];

					promises.push(
						linkManager.sendCreateRequest({
							objectAPortfolioID: itemPortfolioId,
							objectBPortfolioID: linkedXyiconPortfolioId,
							xyiconLinkDetails: createData,
						}),
					);

					await Promise.all(promises);

					this.forceUpdate();
				});
			} else if (sourceDroppableId !== destination.droppableId && !portMap[destination?.droppableId]?.find((o) => o.xyiconId === xyiconId)) {
				this.forceUpdate();

				this._activeRequests++;

				// Handle link moving in the virtual list
				const sourcePort = this._portMap.portMap[sourceDroppableId];
				const destinationPort = this._portMap.portMap[destination.droppableId] || [];

				this._portMap.portMap[sourceDroppableId] = sourcePort.filter((o) => o.link.id !== existingLinkMaybe?.id);
				this._portMap.portMap[destination.droppableId] = [...destinationPort, {xyiconId, link: existingLinkMaybe}];

				if (this._portMap.portMap[sourceDroppableId].length === 0) {
					delete this._portMap.portMap[sourceDroppableId];
				}

				// Handle link moving in the backend
				runInAction(async () => {
					const promises: Promise<any>[] = [];

					promises.push(LinkBreakers.breakLinks(app.spaceViewRenderer.transport, [existingLinkMaybe.id], false, itemPortfolioId));

					const createData: XyiconLinkDetail[] = [
						{
							objectAID: item.id,
							objectBID: xyiconId,
							objectAPortID: destination.droppableId,
							objectBPortID:
								(existingLinkMaybe?.objectAID === xyiconId ? existingLinkMaybe?.objectAPortID : existingLinkMaybe?.objectBPortID) || null,
							isEmbedded: item.ownFeature === XyiconFeature.Xyicon ? item.isEmbedded : false,
						},
					];

					promises.push(
						linkManager.sendCreateRequest({
							objectAPortfolioID: itemPortfolioId,
							objectBPortfolioID: linkedXyiconPortfolioId,
							xyiconLinkDetails: createData,
						}),
					);

					await Promise.all(promises);
				});
			}
		}
	};

	private onBeforeCapture = (initial: BeforeCapture) => {
		//Please do not delete it, this is for debugging purposes.
		//console.log(initial, `onBeforeCapture, draggableId: ${initial.draggableId}`)
		const xyiconId = initial.draggableId.split(DRAGGABLE_ID_SEPARATOR)[1];

		this.setState({
			grabbedXyiconId: xyiconId,
			grabbedItemDraggableId: initial.draggableId,
		});
	};

	private onDragStart = (initial: DragStart) => {
		// const {source, draggableId} = initial;
		//Please do not delete it, this is for debugging purposes.
		//console.log(source, `onDragStart, source: ${source.droppableId}, draggableId: ${draggableId}`)
	};

	private onDragUpdate = (initial: DragUpdate, provided: ResponderProvided) => {
		//Please do not delete it, this is for debugging purposes.
		//console.log(initial, `onDragUpdate, source: ${initial.source.droppableId} | ${initial.source.index}, destination: ${initial.destination?.droppableId} | ${initial.destination?.index}, draggableId: ${initial.draggableId}`)
	};

	private onDragEnd = (result: DropResult) => {
		//Please do not delete it, this is for debugging purposes.
		//console.log(`onDragEnd, source: ${result.source?.droppableId} | ${result.source?.index}, destination: ${result.destination?.droppableId} | ${result.destination?.index}, draggableId: ${result?.draggableId}`)

		this.movePortComponentLink(result);

		this.setState({
			grabbedXyiconId: "",
			grabbedItemDraggableId: "",
		});
	};

	private updateLocalLists(props: IPortsProps) {
		this._orphanedList = this.getOrphanedXyiconLinks(props);
		this._portMap = Ports.getPortMapAndLabelOverrides(props.item.ownFeature, props.item, props.appState.actions);

		this._disposer?.();
		if (props.item.ownFeature === XyiconFeature.Xyicon) {
			this._disposer = reaction(() => JSON.stringify((props.item as Xyicon).catalog.portTemplate), this.onPortTemplateChange);
		}
	}

	private onLinksUpdated = () => {
		clearTimeout(this._updateTimeoutId);
		this._updateTimeoutId = window.setTimeout(() => {
			this.updateLocalLists(this.props);
		}, waitAmountBeforeUIUpdate);
	};

	private onLabelChange = (newValue: string, portId: string) => {
		const labelOverrideMaybe = this._portMap.labelOverrides.find((l) => l.id === portId);

		if (labelOverrideMaybe) {
			if (labelOverrideMaybe.label === newValue) {
				// No change, don't call BE API for change
				return;
			} else {
				labelOverrideMaybe.label = newValue;
			}
		} else {
			this._portMap.labelOverrides.push({id: portId, label: newValue});
		}

		return this.props.onLabelChange?.(newValue, portId);
	};

	public override componentDidMount(): void {
		this.updateLocalLists(this.props);
		this.props.app.transport.signalR.listener.signals.linksUpdated.add(this.onLinksUpdated);
	}

	public override UNSAFE_componentWillReceiveProps(nextProps: IPortsProps) {
		this.updateLocalLists(nextProps);
	}

	public override componentWillUnmount(): void {
		this._disposer?.();
		this.props.app.transport.signalR.listener.signals.linksUpdated.remove(this.onLinksUpdated);
	}

	private readonly getUserXyiconPermission = (item: Xyicon) => {
		return this.props.appState.actions.getModuleTypePermission(item?.typeId, XyiconFeature.Xyicon, item?.portfolioId);
	};

	public override render() {
		const {item, appState, layout, app, onEditPortTemplateClick, onLabelChange} = this.props;
		const {grabbedXyiconId, grabbedItemDraggableId} = this.state;
		const isCatalog = item.ownFeature === XyiconFeature.XyiconCatalog;
		const portTemplate = isCatalog ? item.portTemplate : item.catalog.portTemplate;
		const lastChildHasChild = Port.lastChildHasChild(portTemplate);
		const {portMap, labelOverrides} = this._portMap;
		const crossPortfolioLinks = appState.actions.getCrossPortfolioLinksXyiconXyicon(item.id);
		// After we delete the orphaned links, it takes a couple ms to receive the signalr events
		// So we need to filter out the non-existent (deleted) crossportfolio orphaned links instead,
		// to prevent runtime errors
		const orphanedLinks = this._orphanedList.filter((o) => {
			if (o.objectAPortfolioID === o.objectBPortfolioID) {
				return true;
			} // crossportfolio links
			else {
				const crossPortfolioLinkMaybe = crossPortfolioLinks.find((cpl) => cpl.link.id === o.id);

				return !!crossPortfolioLinkMaybe;
			}
		});
		const isParentXyiconViewOnlyMode = orphanedLinks.length > 0 && !isCatalog && this.getUserXyiconPermission(item) < Permission.Update;
		const isBreakLinkButtonVisible =
			!isParentXyiconViewOnlyMode &&
			orphanedLinks.every((ox: Link) => {
				const xyiconId = ox.objectAID === item.id ? ox.objectBID : ox.objectAID;
				const xyicon = appState.actions.getFeatureItemById<Xyicon>(xyiconId, XyiconFeature.Xyicon);
				return xyicon && this.getUserXyiconPermission(xyicon) >= Permission.Update;
			});

		return (
			<div className={ReactUtils.cls("PortContainer", {dndActive: grabbedXyiconId})}>
				{onEditPortTemplateClick && (
					<Button
						className="editPortTemplateButton"
						label="Edit"
						title="Edit Port Template"
						onClick={onEditPortTemplateClick}
						disabled={this.catalogPermission < Permission.Update}
					/>
				)}
				<DragDropContext
					onBeforeCapture={this.onBeforeCapture}
					onDragStart={this.onDragStart}
					onDragUpdate={this.onDragUpdate}
					onDragEnd={this.onDragEnd}
				>
					{!!orphanedLinks.length && (
						<div className="orphanedLinks">
							<h3>{`Orphaned Xyicon Count: ${orphanedLinks.length}`}</h3>
							<p>
								Due to the port template change or a xyicon model update, the following xyicons are no longer linked to a port. To re-link, drag the
								xyicons to the desired port.
							</p>
							<Droppable
								droppableId={orphanedDroppableId}
								direction="horizontal"
								isDropDisabled={true}
							>
								{(provided) => (
									<div
										ref={provided.innerRef}
										{...provided.droppableProps}
										className="hbox orphaned flex_1"
									>
										{orphanedLinks.map((ox: Link, index: number) => {
											const xyiconId = ox.objectAID === item.id ? ox.objectBID : ox.objectAID;
											const xyicon = appState.actions.getFeatureItemById<Xyicon>(xyiconId, XyiconFeature.Xyicon);
											const portId = -1; // orphaned, therefore it's not attached to a part, so as a convention, we use -1
											const dragId = `${ox.id}${DRAGGABLE_ID_SEPARATOR}${xyiconId}${DRAGGABLE_ID_SEPARATOR}${portId}`;
											const isXyiconViewOnlyMode = isParentXyiconViewOnlyMode || this.getUserXyiconPermission(xyicon) < Permission.Update;

											if (xyicon) {
												return (
													<Draggable
														draggableId={dragId}
														key={dragId}
														index={index}
														isDragDisabled={isXyiconViewOnlyMode}
													>
														{(provided, snapshot) => (
															<div
																{...provided.draggableProps}
																{...provided.dragHandleProps}
																ref={provided.innerRef}
																className={ReactUtils.cls("link", {dragging: snapshot.isDragging})}
															>
																<PortComponentLink
																	xyicon={xyicon}
																	layout={this.props.layout}
																	isDraggingActive={false}
																	link={ox}
																	disabled={isXyiconViewOnlyMode}
																/>
															</div>
														)}
													</Draggable>
												);
											} else {
												const isCrossPortfolioLink = ox.objectALinkType === LinkType.Xyicon && ox.objectBLinkType === LinkType.Xyicon;

												if (isCrossPortfolioLink) {
													const linkData = crossPortfolioLinks.find((o) => o.link.id === ox.id);

													if (linkData) {
														return (
															<CrossPortfolioXyicon
																key={linkData.link.id}
																transport={app.transport}
																linkData={linkData}
																showIconOnly={layout === PortLayoutType.Icon}
																showBreakLinkButton={false}
																showDeleteButton={false}
																portfolioId={(item as Xyicon).portfolioId}
															/>
														);
													}
												}

												// The linked object is a boundary. This shouldn't happen, but for extra safety...
												return null;
											}
										})}
									</div>
								)}
							</Droppable>
							{isBreakLinkButtonVisible && (
								<Button
									label="Break Links"
									className="primary"
									onClick={() => this.breakLinks(orphanedLinks)}
								/>
							)}
						</div>
					)}
					<div className="childrenContainer">
						{portTemplate.map((port: PortTemplateDto, index: number) => {
							const id = port.id;
							const lastChild = index === portTemplate.length - 1;

							return (
								<PortComponent
									key={`${item.id}_${id}`}
									item={item}
									id={id}
									children={port.children}
									portMap={portMap}
									feature={item.ownFeature}
									label={port.label}
									isReadOnly={port.isReadOnly}
									isStructurallyEditable={false}
									lastChildHasChild={lastChild && lastChildHasChild}
									onLabelChange={onLabelChange ? this.onLabelChange : null}
									labelOverrides={labelOverrides}
									layout={layout}
									grabbedItemDraggableId={grabbedItemDraggableId}
								/>
							);
						})}
					</div>
				</DragDropContext>
			</div>
		);
	}
}
